import { toast } from '@postscript/components';
import React, {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { logEvent, PRODUCT_AREAS } from 'utils/events';

import { getGuideDismissalKey } from 'components/launchpad/constants';
import { useUser } from 'controllers/contexts/user';
import {
  BRAND_CENTER_SAVED_STEP_KEY,
  BUNDLED_AI_FEATURE_KEY,
  INFINITY_TESTING_TRIED_STEP_KEY,
  PS_APP_PROG_ONBOARDING_MANAGED_FEATURE_KEY,
  PS_APP_PROG_ONBOARDING_TOUCHLESS_FEATURE_KEY,
  RCS_FEATURE_KEY,
  SET_UP_SHOPPER_AUTOMATIONS_STEP_KEY,
} from 'providers/ProgressiveOnboarding/constants/constants';
import {
  managedSteps,
  touchlessSteps,
} from 'providers/ProgressiveOnboarding/constants/groupedSteps';
import {
  useGetOnboardingStates,
  useMarkStepCompleted,
} from 'providers/ProgressiveOnboarding/queries';
import {
  createGuide,
  getMappedFeatureKey,
} from 'providers/ProgressiveOnboarding/utils';
import { createSafeContext } from 'utils/createSafeContext';

import {
  BCC,
  RCS_SENDER_APPLICATION,
  SHOPPER_2_ONBOARDING,
} from 'components/admin/utils/feature-flags';
import { useInfinityTestingBillingStatus } from 'components/billing/modules/plans/hooks/useInfinityTestingBillingStatus';
import { useShopperBillingStatus } from 'components/billing/modules/plans/hooks/useShopperBillingStatus';
import { isRcsSettingsEligible } from 'components/rcs/isRcsSettingsEligible';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useShopperStatus } from 'hooks/useShopperStatus';
import { useIsAIActivationEligible } from 'providers/entitlements/useIsAIActivationEligible';
import type {
  Guide,
  IMarkStepCompleted,
  OnboardingState,
} from 'providers/ProgressiveOnboarding/types';

interface ProgressiveOnboardingContext {
  /** Guides that still have incomplete steps */
  availableGuides: Guide[];

  /** All guides including completed ones (respects feature flags) */
  allGuides: Guide[];

  /** Currently active guide - open in the side drawer */
  activeGuide?: Guide;

  /** Function to set active guide */
  setActiveGuide: Dispatch<Guide | undefined>;

  markStepCompleted: ({
    guideKey,
    stepKey,
    metadata,
  }: IMarkStepCompleted) => void;

  /** Guide keys the user has dismissed from the launchpad sidebar. */
  dismissedGuideKeys: ReadonlySet<string>;

  /** Dismiss a guide from the launchpad sidebar (persists, logs, toasts). */
  dismissGuide: (params: { guideKey: string; setupName: string }) => void;

  /** Loading state */
  isLoading: boolean;

  /** Error state */
  error: unknown;
}

export const [Provider, useProgressiveOnboarding] =
  createSafeContext<ProgressiveOnboardingContext>(
    'ProgressiveOnboardingContext',
    'useProgressiveOnboarding must be a descendant of ProgressiveOnboardingProvider',
  );

interface ProgressiveOnboardingProviderProps {
  children?: React.ReactNode;
}

export const ProgressiveOnboardingProvider = ({
  children,
}: ProgressiveOnboardingProviderProps) => {
  const { data: onboardingStates, isLoading, error } = useGetOnboardingStates();
  const { mutate: markStepCompleted } = useMarkStepCompleted();
  const { hasLabsFlag } = usePSLabs();
  const hasBCFlagEnabled = hasLabsFlag(BCC);
  // Gates the Shopper Automations onboarding step (open beta). Kept consistent
  // with the flag that gates Automations access in Shopper config.
  const hasShopperAutomationsOnboarding = hasLabsFlag(SHOPPER_2_ONBOARDING);

  const isAIActivationEligible = useIsAIActivationEligible();
  const {
    isEligible: isEligibleForInfinityTesting,
    hasProduct: hasInfinityTestingProduct,
  } = useInfinityTestingBillingStatus();
  const {
    user: {
      customer_segment: customerSegment,
      is_cs_led: isCSLed,
      shop_id: shopId,
    },
  } = useUser();
  const isRcsEligible = isRcsSettingsEligible(
    customerSegment,
    hasLabsFlag(RCS_SENDER_APPLICATION),
  );

  const { hasShopperFeatureEnabled } = useShopperStatus();
  const { isEligible: isEligibleForShopper } = useShopperBillingStatus();

  const showBrandCenterStep =
    isEligibleForInfinityTesting ||
    isEligibleForShopper ||
    hasBCFlagEnabled ||
    hasInfinityTestingProduct ||
    hasShopperFeatureEnabled;

  const featureFlaggedGuides = (state: OnboardingState) => {
    if (state.featureKey === BUNDLED_AI_FEATURE_KEY) {
      return isAIActivationEligible;
    }
    if (state.featureKey === RCS_FEATURE_KEY) {
      return isRcsEligible;
    }

    return true;
  };

  const mapStateToGuide = (state: OnboardingState) => {
    const mappedFeatureKey = getMappedFeatureKey(
      state.featureKey,
      isCSLed ?? false,
    );

    const stepFilterMap: Record<string, readonly string[]> = {
      [PS_APP_PROG_ONBOARDING_TOUCHLESS_FEATURE_KEY]: touchlessSteps,
      [PS_APP_PROG_ONBOARDING_MANAGED_FEATURE_KEY]: managedSteps,
    };

    const allowedSteps = stepFilterMap[mappedFeatureKey];
    let filteredStates = allowedSteps
      ? state.states.filter((step) => allowedSteps.includes(step.stepKey))
      : state.states;

    if (!showBrandCenterStep) {
      filteredStates = filteredStates.filter(
        (step) => step.stepKey !== BRAND_CENTER_SAVED_STEP_KEY,
      );
    }
    if (!isEligibleForInfinityTesting) {
      filteredStates = filteredStates.filter(
        (step) => step.stepKey !== INFINITY_TESTING_TRIED_STEP_KEY,
      );
    }
    if (!hasShopperAutomationsOnboarding) {
      filteredStates = filteredStates.filter(
        (step) => step.stepKey !== SET_UP_SHOPPER_AUTOMATIONS_STEP_KEY,
      );
    }

    return createGuide({
      ...state,
      featureKey: mappedFeatureKey,
      states: filteredStates,
    });
  };

  const guideDeps = [
    onboardingStates,
    featureFlaggedGuides,
    isCSLed,
    isRcsEligible,
    showBrandCenterStep,
    isEligibleForInfinityTesting,
    hasShopperAutomationsOnboarding,
  ];

  const availableGuides = useMemo(
    () =>
      onboardingStates
        ?.filter(featureFlaggedGuides)
        .map(mapStateToGuide)
        .filter((guide) =>
          guide.steps.some((step) => step.state !== 'complete'),
        ) ?? [],
    guideDeps,
  );

  const allGuides = useMemo(
    () =>
      onboardingStates?.filter(featureFlaggedGuides).map(mapStateToGuide) ?? [],
    guideDeps,
  );

  const [activeGuide, setActiveGuide] = useState<Guide>();

  const [dismissedGuideKeys, setDismissedGuideKeys] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    if (!shopId || allGuides.length === 0) return;
    const next = new Set<string>();

    for (const guide of allGuides) {
      try {
        if (
          localStorage.getItem(getGuideDismissalKey(shopId, guide.key)) ===
          'true'
        ) {
          next.add(guide.key);
        }

        for (const step of guide.steps) {
          if (
            localStorage.getItem(getGuideDismissalKey(shopId, step.key)) ===
            'true'
          ) {
            next.add(step.key);
          }
        }
      } catch {
        // localStorage unavailable
      }
    }
    setDismissedGuideKeys((prev) => {
      if (prev.size === next.size && [...prev].every((k) => next.has(k))) {
        return prev;
      }
      return next;
    });
  }, [shopId, allGuides]);

  const dismissGuide = useCallback(
    ({ guideKey, setupName }: { guideKey: string; setupName: string }) => {
      logEvent('launchpad launcher progress dismissed', {
        location: 'nav menu',
        setup_name: setupName,
        product_area: PRODUCT_AREAS.PROGRESSIVE_ONBOARDING,
      });
      if (shopId) {
        try {
          localStorage.setItem(getGuideDismissalKey(shopId, guideKey), 'true');
        } catch {
          // localStorage unavailable
        }
      }
      setDismissedGuideKeys((prev) => {
        if (prev.has(guideKey)) return prev;
        const next = new Set(prev);
        next.add(guideKey);
        return next;
      });
      toast.success('You can continue this setup from the Launchpad.');
    },
    [shopId],
  );

  return (
    <Provider
      value={{
        availableGuides,
        allGuides,
        activeGuide,
        setActiveGuide,
        markStepCompleted,
        dismissedGuideKeys,
        dismissGuide,
        isLoading,
        error,
      }}
    >
      {children}
    </Provider>
  );
};
